import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { useUserContextController } from '../../context/UserContext';
import { useSnackbar } from '../../context/SnackbarContext';
import {
  useCampaignContextController,
  useSpreadsheetViewType,
  setCurrentCampaign,
  setCurrentCampaignLoading,
  setCampaignId,
  setCampaignLock
} from '../../context/CampaignContext';
import { TargetPersonaSearchProvider } from '../../context/TargetPersonaSearchContext';
import { useCampaignLock } from '../../hooks/useCampaignLock';
import LoadingComponent from '../../components/loading';
import { getCampaign, CampaignProspectType } from '../../api/campaigns';
import CampaignPageTopbar from '../../components/topbar/CampaignPageTopbar';
import Sidebar from '../../components/SideBar/SideBar';
import Spreadsheet from '../../components/spreadsheet/Spreadsheet';
import type {
  ColumnHeaderClickInfo,
  SpreadsheetRefHandle
} from '../../components/spreadsheet/Spreadsheet';
import type { SpreadsheetColumnsChangedDetail } from '../../components/spreadsheet/spreadsheetTypes';
import { shiftColumnIndexKeyedRecord } from '../../components/spreadsheet/spreadsheetUtils';
import type { ColumnUpdatedPayload } from '../../components/SideBar/ColumnEditorPanel';
import type { ColumnDefinition } from '../../api/spreadsheet';
import { resolveCampaignHeaderBackground } from '../../theme';

/** Passed via `navigate(..., { state })` (e.g. onboarding → campaign). */
type CampaignLocationState = { openPrioritization?: boolean };

const CampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as
    | CampaignLocationState
    | null
    | undefined;
  const autoOpenPrioritizationFromOnboarding =
    locationState?.openPrioritization === true;
  const [userContext] = useUserContextController();
  const { showSnackbar } = useSnackbar();
  const { user } = userContext;
  const [campaignState, dispatch] = useCampaignContextController();
  const { currentCampaign, currentCampaignLoading, canEdit, crmSyncActive } =
    campaignState;
  const effectiveCanEdit = canEdit && !crmSyncActive;
  const spreadsheetViewType = useSpreadsheetViewType();
  const [isToolPanelOpen, setIsToolPanelOpen] = useState(false);
  const [columnEditorColumn, setColumnEditorColumn] =
    useState<ColumnHeaderClickInfo | null>(null);
  /** True while enrichment or prioritization holds the sheet lock (column editor, sync, sidebar write ops). */
  const [spreadsheetJobLocked, setSpreadsheetJobLocked] = useState(false);
  /** Enrichment only — for UI copy that must not mention enrichment during prioritization. */
  const [
    spreadsheetLockedDuringEnrichment,
    setSpreadsheetLockedDuringEnrichment
  ] = useState(false);
  const [isProspectPrioritizationActive, setIsProspectPrioritizationActive] =
    useState(false);
  const [aiGtmEngineerExpanded, setAiGtmEngineerExpanded] = useState(false);
  const [columnNameOverrides, setColumnNameOverrides] = useState<
    Record<number, string>
  >({});
  const [columnSchemaOverrides, setColumnSchemaOverrides] = useState<
    Record<
      number,
      { col_type?: string; description?: string; definition?: ColumnDefinition }
    >
  >({});
  const spreadsheetRef = useRef<SpreadsheetRefHandle | null>(null);
  const theme = useTheme();

  const handleGtmFullScreenExitFromColumnNav = useCallback(() => {
    setAiGtmEngineerExpanded(false);
    setColumnEditorColumn(null);
  }, []);

  const handleOpenColumnEditorForSelection = useCallback(() => {
    if (spreadsheetJobLocked) {
      showSnackbar(
        spreadsheetLockedDuringEnrichment
          ? 'Spreadsheet is locked during enrichment. Please wait for it to complete.'
          : 'Spreadsheet is busy. Please wait for the current operation to complete.',
        'info'
      );
      return false;
    }
    const info =
      spreadsheetRef.current?.getColumnEditorInfoForSelection() ?? null;
    if (info) {
      setColumnEditorColumn(info);
      return true;
    }
    showSnackbar('Select a cell to edit its column', 'info');
    return false;
  }, [showSnackbar, spreadsheetJobLocked, spreadsheetLockedDuringEnrichment]);

  const handleAutoOpenPrioritizationConsumed = useCallback(() => {
    const st = location.state as CampaignLocationState | null | undefined;
    if (st?.openPrioritization !== true) return;
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate]);

  const handleColumnsChanged = useCallback(
    (detail?: SpreadsheetColumnsChangedDetail) => {
      if (
        detail?.kind === 'insert-col' ||
        detail?.kind === 'remove-col' ||
        detail?.kind === 'reorder-cols'
      ) {
        setColumnNameOverrides((o) => shiftColumnIndexKeyedRecord(o, detail));
        setColumnSchemaOverrides((o) => shiftColumnIndexKeyedRecord(o, detail));
      }
      if (!columnEditorColumn) return;
      const info =
        spreadsheetRef.current?.getColumnEditorInfoForSelection() ?? null;
      if (info) {
        setColumnEditorColumn(info);
      } else {
        setColumnEditorColumn(null);
      }
    },
    [columnEditorColumn]
  );

  // Keep campaign id in context for child components; reset lock when leaving
  useEffect(() => {
    if (campaignId) {
      setCampaignId(dispatch, campaignId);
    }
    return () => {
      setCampaignId(dispatch, null);
      setCampaignLock(dispatch, { canEdit: true, lockedByUserName: null });
    };
  }, [campaignId, dispatch]);

  // Manage lock lifecycle: acquire on mount, release on unmount, poll to extend
  useCampaignLock(campaignId, user?.fullName ?? '');

  /**
   * Clear state when spreadsheet view type changes (account ↔ lead):
   * - Close panels
   * - Clear column overrides
   */
  useEffect(() => {
    setColumnEditorColumn(null);
    setColumnNameOverrides({});
    setColumnSchemaOverrides({});
    setAiGtmEngineerExpanded(false);
    setIsProspectPrioritizationActive(false);
  }, [spreadsheetViewType]);

  // Close column editor when spreadsheet becomes locked (enrichment or prioritization)
  useEffect(() => {
    if (spreadsheetJobLocked) {
      setColumnEditorColumn(null);
    }
  }, [spreadsheetJobLocked]);

  // Fetch when `campaignId` (URL) changes only. Do not list `currentCampaign` in deps:
  // when the fetch completes, `currentCampaign?.id` updates and would re-run this effect,
  // fire cleanup (isCancelled), and race with the async `finally` block.
  useEffect(() => {
    // Early return if campaignId is missing
    if (!campaignId) {
      showSnackbar('Campaign ID is missing', 'error');
      navigate('/campaign');
      return;
    }

    // Skip fetch if context already has this campaign (e.g. navigate back without unmount).
    if (currentCampaign?.id === campaignId) {
      setCurrentCampaignLoading(dispatch, false);
      return;
    }

    let isCancelled = false;

    const fetchCampaignDetails = async () => {
      setCurrentCampaignLoading(dispatch, true);
      try {
        const campaignData = await getCampaign(campaignId);
        if (!isCancelled) {
          if (campaignData.archived) {
            showSnackbar('This campaign is archived', 'error');
            navigate('/campaign');
            return;
          }
          setCurrentCampaign(dispatch, campaignData);
        }
      } catch (error: any) {
        if (!isCancelled) {
          showSnackbar(
            error?.message || 'Failed to load campaign details',
            'error'
          );
          navigate('/campaign');
        }
      } finally {
        if (!isCancelled) {
          setCurrentCampaignLoading(dispatch, false);
        }
      }
    };

    fetchCampaignDetails();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when `campaignId` changes; `currentCampaign` is read for a guard on that transition
  }, [campaignId, dispatch, navigate, showSnackbar]);

  if (!user) {
    return <LoadingComponent message="Fetching user data..." />;
  }

  if (currentCampaignLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%'
        }}
      >
        <LoadingComponent message="Fetching campaign data..." />
      </Box>
    );
  }

  if (!currentCampaign) {
    return null;
  }

  const isAccountCampaign =
    currentCampaign.prospect_type === CampaignProspectType.ACCOUNT;

  const mainRowChromeBg = resolveCampaignHeaderBackground(
    theme.customColors,
    currentCampaign.prospect_type,
    spreadsheetViewType
  );

  return (
    <TargetPersonaSearchProvider>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          flexDirection: 'column'
        }}
      >
        <CampaignPageTopbar
          spreadsheetRef={spreadsheetRef}
          getFilteredOutRowIds={() =>
            spreadsheetRef.current?.getFilteredOutRowIds() ?? null
          }
        />
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            backgroundColor: mainRowChromeBg,
            transition: 'background-color 0.2s ease'
          }}
        >
          {canEdit && (
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
                alignItems: 'flex-start',
                flex: aiGtmEngineerExpanded ? 1 : undefined,
                minWidth: 0
              }}
            >
              <Sidebar
                key={spreadsheetViewType}
                isManager={user.isManager}
                isAdmin={user.isAdmin}
                hasAccessToAutomation={user.hasAccessToAutomation}
                isBeta={user.isBeta}
                columnEditorColumn={columnEditorColumn}
                spreadsheetJobLocked={spreadsheetJobLocked}
                spreadsheetLockedDuringEnrichment={
                  spreadsheetLockedDuringEnrichment
                }
                autoOpenPrioritization={autoOpenPrioritizationFromOnboarding}
                onAutoOpenPrioritizationConsumed={
                  handleAutoOpenPrioritizationConsumed
                }
                aiGtmEngineerExpanded={aiGtmEngineerExpanded}
                onAiGtmEngineerExpandedChange={setAiGtmEngineerExpanded}
                onGtmFullScreenExitFromColumnNav={
                  handleGtmFullScreenExitFromColumnNav
                }
                spreadsheetRef={spreadsheetRef}
                isProspectPrioritizationActive={isProspectPrioritizationActive}
                onColumnEditorClose={() => setColumnEditorColumn(null)}
                onOpenColumnEditorForSelection={
                  handleOpenColumnEditorForSelection
                }
                onPanelStateChange={setIsToolPanelOpen}
                getEnrichmentSelection={() =>
                  spreadsheetRef.current?.getEnrichmentSelection() ?? null
                }
                getFilteredOutRowIds={() =>
                  spreadsheetRef.current?.getFilteredOutRowIds() ?? null
                }
                onGtmEngineerComplete={() =>
                  spreadsheetRef.current?.runSpreadsheetSync()
                }
                onColumnUpdated={async (updates: ColumnUpdatedPayload) => {
                  const columnIndex = updates.columnIndex ?? null;
                  const applyOverrides = (idx: number) => {
                    if (updates.columnName != null) {
                      setColumnNameOverrides((o) => ({
                        ...o,
                        [idx]: updates.columnName!
                      }));
                    }
                    if (
                      'columnType' in updates ||
                      'description' in updates ||
                      'definition' in updates
                    ) {
                      setColumnSchemaOverrides((o) => ({
                        ...o,
                        [idx]: {
                          ...o[idx],
                          ...('columnType' in updates && {
                            col_type: updates.columnType
                          }),
                          ...('description' in updates && {
                            description: updates.description
                          }),
                          ...('definition' in updates && {
                            definition: updates.definition
                          })
                        }
                      }));
                    }
                  };
                  if (columnIndex != null) {
                    applyOverrides(columnIndex);
                  }
                  setColumnEditorColumn((prev) => {
                    if (!prev) return null;
                    if (columnIndex != null && columnIndex !== prev.columnIndex)
                      return prev;
                    return { ...prev, ...updates };
                  });
                }}
              />
            </Box>
          )}
          {!aiGtmEngineerExpanded && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                paddingBottom: '8px',
                overflow: 'auto',
                boxSizing: 'border-box',
                backgroundColor: (theme) => theme.customColors.themeSecondary,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: '15px 15px 0px 0px',
                transition:
                  'flex-basis 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-left-color 0.2s ease'
              }}
            >
              <Spreadsheet
                ref={spreadsheetRef}
                key={`${campaignState.spreadsheetDataVersion}-${spreadsheetViewType}`}
                onSpreadsheetLockChange={setSpreadsheetJobLocked}
                onEnrichmentSpreadsheetLockChange={
                  setSpreadsheetLockedDuringEnrichment
                }
                onProspectPrioritizationActivityChange={
                  setIsProspectPrioritizationActive
                }
                onColumnHeaderClick={
                  effectiveCanEdit && !spreadsheetJobLocked
                    ? (info) =>
                        setColumnEditorColumn((prev) => {
                          if (!prev) return info;
                          if (prev.columnIndex === info.columnIndex)
                            return null;
                          return info;
                        })
                    : undefined
                }
                onColumnDeselect={() => setColumnEditorColumn(null)}
                onColumnsChanged={
                  effectiveCanEdit ? handleColumnsChanged : undefined
                }
                columnNameOverrides={
                  Object.keys(columnNameOverrides).length > 0
                    ? columnNameOverrides
                    : undefined
                }
                columnSchemaOverrides={
                  Object.keys(columnSchemaOverrides).length > 0
                    ? columnSchemaOverrides
                    : undefined
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    </TargetPersonaSearchProvider>
  );
};

export default CampaignDetailPage;
